import React, { useState, useMemo } from 'react';
import { Badge } from '../../../catalyst/badge';
import { Search, PlusCircle, Download } from 'lucide-react';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import { useHoursOfRestData } from '../../../../hooks/domain/useHoursOfRestData';
import { useVessel } from 'contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
import Dropdown from '../../../common/Dropdown';
import HoursOfRestSidebar from './HoursOfRestSidebar';
import HoursRecordView from './HoursRecordView';
import HoursView from './HoursView';
import {
  getComplianceSummary,
  getWeeklyStats,
  getCrewWithIssues,
  getLastWeekDates,
  checkRolling24HourCompliance,
  calculateWeeklyRest,
  getLatestDayRest,
  getDaysRecordedThisMonth,
  getCurrentMonthKey
} from '../../../../utils/hoursComplianceHelpers';

const HoursOfRestModule = ({ onRecordHoursOpen, preSelectedCrewId }) => {
  const [showRecordView, setShowRecordView] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [selectedCrewMember, setSelectedCrewMember] = useState(null);
  const [initialSelectedCrew, setInitialSelectedCrew] = useState(null);

  // Filter state
  // FT-30: Removed regimeFilter - WBC3 seagoing only
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'compliant' | 'non-compliant'

  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  // Use the NEW unified crew system
  const { crew } = useCrew();
  const { can, getDeniedMessage } = usePermissions();
  const { showAlert, AlertDialog } = useCustomDialogs();
  
  // 🎉 PHASE 3: Using the new domain hook with month buckets!
  const horApi = useHoursOfRestData();
  // For backward compatibility, create a hoursData object from the flat data
  const hoursData = React.useMemo(() => {
    const result = {};
    horApi.data.forEach(record => {
      if (!result[record.crewId]) {
        result[record.crewId] = {};
      }
      // Include ALL fields from the record for proper display
      result[record.crewId][record.date] = {
        workHours: record.workHours,
        restHours: record.restHours,
        totalWork: record.workHours, // Add for compatibility
        totalRest: record.restHours, // Add for compatibility
        periods: record.periods, // Include periods array for grid display
        notes: record.notes,
        workStart: record.workStart,
        workEnd: record.workEnd,
        pattern: record.pattern,
        isCompliant: record.isCompliant
      };
    });
    return result;
  }, [horApi.data]);
  
  const setHoursData = (newData) => {
    try {
      // Use saveBatch which accepts both array and nested formats
      const result = horApi.saveBatch(newData);
      // Handle both old boolean return and new object return
      return typeof result === 'boolean' ? result : result.success;
    } catch (error) {
      // Error saving data
      return false; // Failure
    }
  };
  
  const reloadHours = () => {
    // Data is automatically reactive via domain hook
  };

  // Set up the callback for external components to open record modal
  React.useEffect(() => {
    if (onRecordHoursOpen) {
      onRecordHoursOpen.current = (crewMember) => {
        // Check permissions before opening record view - no async
        if (!can('update', 'hours_of_rest')) {
          showAlert(
            'Permission Denied',
            'Only Fleet administrators and Skippers can record hours of rest.',
            'error'
          ); // fire-and-forget
          return;
        }
        if (crewMember) {
          setInitialSelectedCrew([crewMember.id]);
        }
        setShowRecordView(true);
      };
    }
  }, [onRecordHoursOpen, can, showAlert]);

  // Handle pre-selected crew from navigation
  React.useEffect(() => {
    if (preSelectedCrewId && crew.length > 0) {
      // Check permissions before opening record view
      if (!can('update', 'hours_of_rest')) {
        showAlert(
          'Permission Denied',
          'Only Fleet administrators and Skippers can record hours of rest.',
          'error'
        );
        return;
      }
      const member = crew.find(c => c.id === preSelectedCrewId);
      if (member) {
        setInitialSelectedCrew([member.id]);
        setShowRecordView(true);
      }
    }
  }, [preSelectedCrewId, crew, can, showAlert]);

  // Get signatures data from the hook for compliance summary
  const signaturesData = React.useMemo(() => {
    // Build signatures lookup from horApi
    const monthKey = getCurrentMonthKey();
    return { [monthKey]: horApi.getSignatures(monthKey) };
  }, [horApi]);

  const compliance = getComplianceSummary(crew, hoursData, signaturesData);
  // Show all non-archived crew (includes both On Duty and Off Duty)
  const activeCrew = crew.filter(c => c.status !== 'archived');
  const weeklyStats = getWeeklyStats(activeCrew, hoursData);
  const crewWithIssues = getCrewWithIssues(activeCrew, hoursData);

  // FT-30: Filter crew - removed horRegime/inland logic, fixed weeklyRest += 24 bug
  const filteredActiveCrew = useMemo(() => {
    return activeCrew.filter(member => {
      const memberHours = hoursData[member.id] || {};
      const lastWeek = getLastWeekDates();

      // FT-30: Fixed - use calculateWeeklyRest which doesn't add 24 for missing days
      const { weeklyRest, daysWithData } = calculateWeeklyRest(memberHours);

      // Check for daily violations (< 10h rest)
      let violations = 0;
      lastWeek.forEach(date => {
        const dayData = memberHours[date];
        if (dayData && dayData.restHours < 10) {
          violations++;
        }
      });

      // Check rolling 24-hour compliance
      const rollingViolations = checkRolling24HourCompliance(memberHours, lastWeek);

      // FT-30: WBC3 seagoing only - crew is compliant if 77h+ weekly rest AND no daily violations
      const isCompliant = daysWithData > 0 && weeklyRest >= 77 && violations === 0 && rollingViolations.length === 0;

      // Search filter
      if (searchTerm) {
        const name = (member.full_name || member.name || '').toLowerCase();
        const position = (member.position || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        if (!name.includes(searchLower) && !position.includes(searchLower)) {
          return false;
        }
      }

      // Status filter
      if (statusFilter === 'compliant' && !isCompliant) return false;
      if (statusFilter === 'non-compliant' && isCompliant) return false;

      // FT-30: Removed regime filter - WBC3 seagoing only

      return true;
    });
  }, [activeCrew, hoursData, searchTerm, statusFilter]);

  // FT-358: register bottom-bar middle-slot actions per brief §D.
  // Record gated on permission; Export always shown; empty while a
  // sub-view (record / view) is active.
  useSetBottomBarActions(
    showRecordView || showView
      ? []
      : [
          can('update', 'hours_of_rest') && {
            key: 'hor-record', icon: PlusCircle, label: 'Record',
            primary: true, onClick: () => setShowRecordView(true),
          },
          {
            key: 'hor-export', icon: Download, label: 'Export',
            onClick: () => {
              const el = document.querySelector('[data-export-hor]');
              if (el) el.click();
            },
          },
        ].filter(Boolean),
  );

  // Show full-screen record view if active
  if (showRecordView) {
    return (
      <HoursRecordView
        onBack={() => {
          setShowRecordView(false);
          setInitialSelectedCrew(null);
        }}
        crew={crew}
        existingData={hoursData}
        initialSelectedCrew={initialSelectedCrew || []}
        onSave={(newData) => {
          // Update the local state immediately
          const result = setHoursData(newData);
          // Also reload from storage to ensure sync
          const success = typeof result === 'boolean' ? result : result?.success;
          if (success) {
            setTimeout(() => reloadHours(), 100);
          }
          return result; // Return full result (boolean or object with error) to HoursRecordView
        }}
      />
    );
  }

  // Show full-screen view for viewing records
  if (showView) {
    return (
      <HoursView
        onBack={() => {
          setShowView(false);
          setSelectedCrewMember(null);
        }}
        selectedCrew={selectedCrew}
        setSelectedCrew={setSelectedCrew}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        crew={crew}
        hoursData={horApi.data}
        crewMember={selectedCrewMember}
        onRecordHours={() => {
          setShowView(false);
          setShowRecordView(true);
        }}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Content */}
      <div className="lg:col-span-2">
        {/* Crew Status List */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          {/* Header - slate-600 with search inside */}
          <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-shrink-0">
                <h3 className="text-base font-semibold text-white whitespace-nowrap">
                  Crew Hours Summary
                </h3>
                <p className="mt-0.5 text-sm text-slate-300 hidden sm:block">Last 7 days compliance status</p>
              </div>

              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search crew by name or position..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-white/20 rounded-md bg-white text-slate-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          </div>

          {/* Filters - below header */}
          <div className="px-6 py-4">
            <div className="flex gap-3">
              <Dropdown
                value={statusFilter}
                onChange={setStatusFilter}
                width="w-auto min-w-[160px]"
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'compliant', label: 'Compliant' },
                  { value: 'non-compliant', label: 'Non-compliant' }
                ]}
              />

{/* Regime filter removed for WBC3 Edition - seagoing only */}
            </div>
          </div>

        <div className="divide-y divide-gray-200">
          {filteredActiveCrew.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {searchTerm || statusFilter !== 'all' ? (
                <>
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="mt-2">No crew members match the current filters.</p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                    }}
                    className="mt-2 text-sm text-cyan-600 hover:text-cyan-800"
                  >
                    Clear all filters
                  </button>
                </>
              ) : (
                <>
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p className="mt-2">No active crew members.</p>
                  <p className="mt-1 text-xs">Add crew members in the Crew module to track hours of rest.</p>
                </>
              )}
            </div>
          ) : (
            filteredActiveCrew.map(member => {
              const memberHours = hoursData[member.id] || {};
              const lastWeek = getLastWeekDates();

              // FT-30: Use new helper functions (no weeklyRest += 24 for missing days)
              const { weeklyRest, daysWithData } = calculateWeeklyRest(memberHours);
              const latestDay = getLatestDayRest(memberHours);
              const monthRecording = getDaysRecordedThisMonth(memberHours);

              // Calculate work hours for display
              let weeklyWork = 0;
              lastWeek.forEach(date => {
                const dayData = memberHours[date];
                if (dayData) {
                  weeklyWork += dayData.workHours || 0;
                }
              });

              // Check for daily violations (< 10h rest)
              let dailyViolations = 0;
              lastWeek.forEach(date => {
                const dayData = memberHours[date];
                if (dayData && dayData.restHours < 10) dailyViolations++;
              });

              // Check rolling 24-hour violations
              const rollingViolations = checkRolling24HourCompliance(memberHours, lastWeek);

              // FT-30: Compliance - WBC3 seagoing rules
              const isCompliant = daysWithData > 0 && weeklyRest >= 77 && dailyViolations === 0 && rollingViolations.length === 0;

              // Get signature status for current month
              const monthKey = getCurrentMonthKey();
              const monthSigs = horApi.getSignatures(monthKey);
              const isSigned = !!(monthSigs?.master && monthSigs?.owner);

              // Color coding for compliance indicators
              const weeklyRestColor = weeklyRest >= 77 ? 'text-green-600' : weeklyRest >= 70 ? 'text-amber-600' : 'text-red-600';
              const dailyRestColor = latestDay && latestDay.restHours >= 10 ? 'text-green-600' : 'text-red-600';

              return (
                <div
                  key={member.id}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedCrew(member.id);
                    setSelectedCrewMember(member);
                    setShowView(true);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900">{member.full_name || member.name}</p>
                      <p className="text-sm text-gray-500">{member.position}</p>

                      {/* FT-30: New compliance display format */}
                      <div className="mt-2 space-y-1">
                        {/* 7-day rest */}
                        <p className="text-sm">
                          <span className="text-gray-600">7-day rest: </span>
                          <span className={`font-medium ${weeklyRestColor}`}>
                            {daysWithData > 0 ? `${weeklyRest.toFixed(0)}h` : 'No data'}
                          </span>
                          {daysWithData > 0 && (
                            <span className="text-gray-400 ml-1">(min 77h)</span>
                          )}
                        </p>

                        {/* Last 24h rest */}
                        <p className="text-sm">
                          <span className="text-gray-600">Last 24h: </span>
                          <span className={`font-medium ${dailyRestColor}`}>
                            {latestDay ? `${latestDay.restHours.toFixed(0)}h` : 'No data'}
                          </span>
                          {latestDay && (
                            <span className="text-gray-400 ml-1">(min 10h)</span>
                          )}
                        </p>

                        {/* Monthly recording status */}
                        <p className="text-xs text-gray-500">
                          {new Date().toLocaleString('default', { month: 'long' })}: {monthRecording.daysRecorded}/{monthRecording.daysElapsed} days recorded
                          {!isSigned && (
                            <span className="text-amber-600 ml-2">• Unsigned</span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 ml-4">
                      {/* Violation warnings */}
                      {(dailyViolations > 0 || rollingViolations.length > 0) && (
                        <div className="text-right text-xs">
                          {dailyViolations > 0 && (
                            <p className="text-red-600">{dailyViolations} daily violation{dailyViolations > 1 ? 's' : ''}</p>
                          )}
                          {rollingViolations.length > 0 && (
                            <p className="text-red-600">{rollingViolations.length} 24h violation{rollingViolations.length > 1 ? 's' : ''}</p>
                          )}
                        </div>
                      )}

                      <Badge color={isCompliant ? 'green' : daysWithData === 0 ? 'zinc' : 'yellow'}>
                        {isCompliant ? 'Compliant' : daysWithData === 0 ? 'No Data' : 'Non-compliant'}
                      </Badge>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <HoursOfRestSidebar 
        compliance={compliance}
        weeklyStats={weeklyStats}
        crewWithIssues={crewWithIssues}
        onRecordHours={() => {
          // Check permissions before opening record view - early return, no async
          if (!can('update', 'hours_of_rest')) {
            showAlert(
              'Permission Denied',
              'Only Fleet administrators and Skippers can record hours of rest.',
              'error'
            ); // fire-and-forget
            return;
          }
          setShowRecordView(true);
        }}
        currentVessel={currentVessel}
      />
      
      {/* Custom dialog for alerts */}
      <AlertDialog />

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}
    </div>
  );
};

export default HoursOfRestModule;